// Retention & Churn Radar — pure, Firebase-free so it unit-tests without the gRPC stack.
// Turns the reconciled client roster (Fresha insights → clientRoster) into a GROWTH surface: real
// retention, and the specific regulars who are slipping away + the revenue they represent. This is the
// #1 barbershop growth lever and the BI tile was showing 0% for it. BEHAVIOR-PIN: BI growth upgrade.
import type { ClientRecord } from '../services/freshaClientImport';

const round1 = (n: number) => Math.round(n * 10) / 10;
const round0 = (n: number) => Math.round(n);
const DAY = 86400000;

export interface ChurnClient {
  name: string;
  phone: string;
  lastVisit: string;      // ISO
  daysSince: number;      // days since last visit
  visits: number;         // lifetime visits
  avgTicket: number;      // totalSpend / visits
  totalSpend: number;     // lifetime spend (LTV)
  cadenceDays: number;    // their usual gap between visits
}

export interface ChurnRadar {
  totalClients: number;
  returning: number;          // >1 lifetime visit
  retentionPct: number;       // returning / total (the honest health-score input)
  rebookedPct: number;        // Fresha "rebooked" flag share
  avgLtv: number;             // avg lifetime spend / client
  avgVisits: number;
  atRisk: {
    count: number;
    recoverableRevenue: number;  // Σ avgTicket over at-risk = one-visit-each win-back value
    clients: ChurnClient[];      // highest-value first, capped
  };
}

export interface ChurnOpts {
  cadenceMultiple?: number;   // lapsed when daysSince > cadence × this (default 1.75)
  minDays?: number;           // floor on the lapse threshold (default 30)
  maxDays?: number;           // ceiling on the lapse threshold (default 120)
  deadAfterDays?: number;     // past this they're "gone", not "at risk" (default 365)
  minVisits?: number;         // must be an established client (default 2)
  cap?: number;               // max at-risk clients returned (default 25)
}

/**
 * Compute retention + the at-risk (churning) client list from the roster as of `nowMs`.
 * At-risk = an established client (≥minVisits) whose gap since last visit has run past their OWN usual
 * cadence (×multiple, clamped) but who isn't so old they're effectively gone. Sorted by avg ticket so the
 * highest-value win-backs surface first.
 */
export function computeChurnRadar(clients: ClientRecord[], nowMs: number, opts: ChurnOpts = {}): ChurnRadar {
  const cadenceMultiple = opts.cadenceMultiple ?? 1.75;
  const minDays = opts.minDays ?? 30;
  const maxDays = opts.maxDays ?? 120;
  const deadAfterDays = opts.deadAfterDays ?? 365;
  const minVisits = opts.minVisits ?? 2;
  const cap = opts.cap ?? 25;

  const n = clients.length;
  let returning = 0, rebooked = 0, visits = 0, spend = 0;
  const atRisk: ChurnClient[] = [];

  for (const c of clients) {
    const v = c.totalVisits || 0;
    const sp = c.totalSpend || 0;
    visits += v; spend += sp;
    if (v > 1) returning++;
    if (c.rebooked) rebooked++;

    if (v >= minVisits && c.lastVisit) {
      const last = Date.parse(c.lastVisit);
      const first = c.firstVisit ? Date.parse(c.firstVisit) : NaN;
      if (!isNaN(last)) {
        const daysSince = Math.floor((nowMs - last) / DAY);
        // their usual gap between visits (fall back to a 4-week barber cadence if we can't derive it)
        const spanDays = !isNaN(first) ? Math.max(1, (last - first) / DAY) : NaN;
        const cadence = !isNaN(spanDays) && v > 1 ? spanDays / (v - 1) : 28;
        const threshold = Math.min(Math.max(cadence * cadenceMultiple, minDays), maxDays);
        if (daysSince > threshold && daysSince <= deadAfterDays) {
          atRisk.push({
            name: c.name, phone: c.phone, lastVisit: c.lastVisit, daysSince,
            visits: v, avgTicket: round0(sp / v), totalSpend: round0(sp), cadenceDays: round0(cadence),
          });
        }
      }
    }
  }

  atRisk.sort((a, b) => b.avgTicket - a.avgTicket || b.totalSpend - a.totalSpend);
  const recoverable = atRisk.reduce((s, c) => s + c.avgTicket, 0);

  return {
    totalClients: n,
    returning,
    retentionPct: n ? round1((returning / n) * 100) : 0,
    rebookedPct: n ? round1((rebooked / n) * 100) : 0,
    avgLtv: n ? round0(spend / n) : 0,
    avgVisits: n ? round1(visits / n) : 0,
    atRisk: { count: atRisk.length, recoverableRevenue: round0(recoverable), clients: atRisk.slice(0, cap) },
  };
}
